import localization from '../../../shared/strings';
import { formatPublishErrorFieldName } from './formatPublishErrorDisplay';

/**
 * Collects field-level publish errors from publishListing failed payload.
 *
 * @param {Array<{ _id: string, error?: object }>} failed
 * @returns {Array<{ fieldName?: string, message: string, ownerType?: string, reason?: string, currentValue?: string }>}
 */
export const collectPublishFieldErrors = (failed = []) => (
  failed.flatMap((item) => {
    const { error } = item || {};
    if (!error) return [];

    if (Array.isArray(error.errors) && error.errors.length) {
      return error.errors.map((fieldError) => ({
        fieldName: fieldError.fieldName || null,
        ownerType: fieldError.ownerType || null,
        message: fieldError.message || error.message,
        reason: fieldError.reason || error.reason || null,
        ...(fieldError.currentValue != null && { currentValue: String(fieldError.currentValue) }),
      }));
    }

    if (error.message) {
      return [{ fieldName: null, message: error.message }];
    }

    return [];
  })
);

const formatSinglePublishErrorToast = (fieldError) => {
  const strings = localization.DIALOGS.PRODUCT_DIALOGS.STATE_LAYERS.PUBLISH_FAILED;
  if (fieldError.fieldName) {
    return strings.SINGLE_ERROR(
      formatPublishErrorFieldName(fieldError.fieldName),
      fieldError.message,
    );
  }
  return fieldError.message;
};

/**
 * Shows toast for a single field error or opens dialog for multiple.
 *
 * @param {Array} failed - publishListing response failed array
 * @param {{ Toast: Function, showPublishFailedDialog: Function }} ui
 * @returns {boolean} true when failures were handled
 */
const handlePublishListingFailures = (failed = [], { Toast, showPublishFailedDialog }) => {
  const fieldErrors = collectPublishFieldErrors(failed);

  if (!fieldErrors.length) {
    Toast('Publishing failed', { type: 'error', autoClose: false });
    return true;
  }

  if (fieldErrors.length === 1) {
    const fieldError = fieldErrors[0];
    const toastMessage = fieldError.fieldName
      ? formatSinglePublishErrorToast(fieldError)
      : (failed[0]?.error?.message || formatSinglePublishErrorToast(fieldError));
    Toast(toastMessage, {
      type: 'error',
      autoClose: false,
    });
    return true;
  }

  showPublishFailedDialog({ errors: fieldErrors });
  return true;
};

export default handlePublishListingFailures;
